import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { AccountantSettingsView, DasOverview, DasView, FinancialAccountView } from "@cx/contracts";
import {
  MEI_ACTIVITIES,
  MEI_ACTIVITY_LABELS,
  formatBRL,
  formatDateBR,
  todayInSaoPaulo,
  type MeiActivity,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { barcodeProblem } from "../gnre/GnreDialogs";
import { collectionLineLabel } from "../gnre/labels";
import { useOpenKey } from "../invoices/InvoiceEvents";
import { DAS_STATUS_LABEL, DAS_STATUS_TONE, competenceLabel, competenceOptions } from "./labels";

/**
 * Contabilidade › DAS MEI (T3.5, RF-CTB-01): valor do mês pela tabela fixa (INSS 5% do salário mínimo + ICMS/ISS
 * conforme a atividade), conta a pagar criada junto, código de barras do PGMEI (o ERP não fala com o governo), baixa
 * manual ou pagamento pelo banco integrado (Asaas/sandbox). Vencido: emitir a guia atualizada no PGMEI.
 */

type Dialog = { kind: "issue" | "pay" | "bank" | "cancel" | "release"; das: DasView } | null;

/** Pagamento pelo banco em curso ou "a conferir": nada de baixa manual, troca de código ou novo pedido. */
const PAYMENT_BUSY: ReadonlySet<string> = new Set(["requested", "submitting", "unknown"]);

function useAccounts(open: boolean) {
  const { api, companyId } = useSession();
  const q = useQuery({
    queryKey: ["fin-accounts", companyId, "list"],
    enabled: open,
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  return (q.data?.data ?? []).filter((a) => a.active);
}

function DasDialog({ dialog, onClose }: { dialog: Dialog; onClose(): void }): ReactElement | null {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const open = dialog !== null;
  const { key, renew } = useOpenKey(open);
  const [barcode, setBarcode] = useState("");
  const [touched, setTouched] = useState(false);
  const [paidAt, setPaidAt] = useState(todayInSaoPaulo());
  const [accountId, setAccountId] = useState("");
  const [reason, setReason] = useState("");
  const accounts = useAccounts(open && (dialog?.kind === "pay" || dialog?.kind === "bank"));
  useEffect(() => {
    if (!open) return;
    setBarcode(dialog.das.digitableLine ?? "");
    setTouched(false);
    setPaidAt(todayInSaoPaulo());
    setReason("");
  }, [open, dialog]);
  useEffect(() => {
    if (!accountId && accounts.length) setAccountId(accounts[0]!.id);
  }, [accountId, accounts]);
  const d = dialog?.das;
  const problem = d && dialog?.kind === "issue" ? barcodeProblem(barcode, d.totalCents) : null;
  const send = useMutation({
    mutationFn: () => {
      const path = {
        issue: "issue",
        pay: "pay",
        bank: "provider-pay",
        cancel: "cancel",
        release: "release-payment",
      }[dialog!.kind];
      const body = {
        issue: { barcode },
        pay: { paidAt, financialAccountId: accountId },
        bank: { financialAccountId: accountId },
        cancel: { reason },
        release: { reason },
      }[dialog!.kind];
      return api<DasView>(`/accounting/das/${d!.id}/${path}`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      toast(
        "success",
        {
          issue: "DAS marcado como emitido",
          pay: "DAS pago e conta a pagar baixada",
          bank: "Pagamento pedido ao banco",
          cancel: "DAS cancelado",
          release: "Pagamento liberado",
        }[dialog!.kind],
      );
      void qc.invalidateQueries({ queryKey: ["accounting", companyId, "das"] });
      onClose();
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  if (!dialog || !d) return null;
  const title = {
    issue: "Informar o código do PGMEI",
    pay: "Marcar como pago",
    bank: "Pagar pelo banco integrado",
    cancel: "Cancelar DAS",
    release: "Liberar pagamento a conferir",
  }[dialog.kind];
  const disabled =
    send.isPending ||
    ((dialog.kind === "pay" || dialog.kind === "bank") && !accountId) ||
    (dialog.kind === "cancel" && reason.trim().length < 5) ||
    (dialog.kind === "release" && reason.trim().length < 10);
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className={`btn ${dialog.kind === "cancel" || dialog.kind === "release" ? "btn-danger" : "btn-primary"}`}
            disabled={disabled}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            Confirmar
          </button>
        </>
      }
    >
      <p className="small">
        DAS {competenceLabel(d.competence)} — {formatBRL(d.totalCents)}, vencimento {formatDateBR(d.dueDate)}.
      </p>
      {dialog.kind === "issue" && (
        <Field
          label="Linha digitável ou código de barras"
          required
          error={touched && problem ? problem : undefined}
          className="col-12"
        >
          <input
            className="form-control"
            data-autofocus
            inputMode="numeric"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
          />
        </Field>
      )}
      {(dialog.kind === "pay" || dialog.kind === "bank") && (
        <div className="row">
          {dialog.kind === "pay" && (
            <Field label="Data do pagamento" required className="col-md-5">
              <input
                type="date"
                className="form-control"
                value={paidAt}
                max={todayInSaoPaulo()}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </Field>
          )}
          <Field label="Conta" required className="col-md-7">
            <select className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.length === 0 && <option value="">Nenhuma conta financeira</option>}
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          {dialog.kind === "bank" && (
            <p className="small text-muted col-12">
              O banco integrado (Asaas) paga pelo saldo da conta dele; a baixa cai nesta conta quando ele
              confirmar.
            </p>
          )}
        </div>
      )}
      {dialog.kind === "release" && (
        <>
          <div className="alert alert-warning small">
            O banco pode ter pago este DAS sem confirmar ao ERP. Confira no Asaas (Pague contas) antes:
            liberar permite pagar de novo, e um pagamento em dobro não volta sozinho.
          </div>
          <Field label="O que você conferiu" required className="col-12">
            <input
              className="form-control"
              value={reason}
              maxLength={255}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </>
      )}
      {dialog.kind === "cancel" && (
        <Field label="Motivo" required className="col-12" hint="A conta a pagar também é cancelada.">
          <input
            className="form-control"
            value={reason}
            maxLength={255}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      )}
    </Modal>
  );
}

function ActivityForm({ current }: { current: MeiActivity | null }): ReactElement {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState<MeiActivity | "">(current ?? "");
  const save = useMutation({
    mutationFn: () =>
      api<AccountantSettingsView>("/accounting/settings", {
        method: "PUT",
        body: JSON.stringify({ meiActivity: v || null }),
      }),
    onSuccess: () => {
      toast("success", "Atividade do MEI salva");
      void qc.invalidateQueries({ queryKey: ["accounting", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="d-flex flex-wrap gap-2 align-items-end">
      <Field label="Atividade do MEI" className="mb-0">
        <select
          className="form-select"
          value={v}
          data-testid="mei-activity"
          onChange={(e) => setV(e.target.value as MeiActivity | "")}
        >
          <option value="">Escolha…</option>
          {MEI_ACTIVITIES.map((a) => (
            <option key={a} value={a}>
              {MEI_ACTIVITY_LABELS[a]}
            </option>
          ))}
        </select>
      </Field>
      <button
        type="button"
        className="btn btn-outline-primary mb-3"
        disabled={!v || save.isPending}
        onClick={() => save.mutate()}
      >
        Salvar atividade
      </button>
    </div>
  );
}

export function DasMeiPage(): ReactElement {
  const { api, companyId, me } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [competence, setCompetence] = useState(todayInSaoPaulo().slice(0, 7));
  const [key, setKey] = useState(() => crypto.randomUUID());
  const ov = useQuery({
    queryKey: ["accounting", companyId, "das"],
    queryFn: () => api<DasOverview>("/accounting/das"),
    refetchInterval: (q) =>
      q.state.data?.data.some((d) => d.paymentStatus === "requested" || d.paymentStatus === "submitting")
        ? 3_000
        : false,
  });
  const generate = useMutation({
    mutationFn: () =>
      api<DasView>("/accounting/das", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ competence }),
      }),
    onSuccess: (d) => {
      toast("success", `DAS ${competenceLabel(d.competence)} gerado com a conta a pagar`);
      setKey(crypto.randomUUID());
      void qc.invalidateQueries({ queryKey: ["accounting", companyId, "das"] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const refresh = useMutation({
    mutationFn: (id: string) => api<DasView>(`/accounting/das/${id}/refresh-payment`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["accounting", companyId, "das"] }),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const o = ov.data;
  const canUpdate = can(perms, "accounting", "update");
  const canPay = canUpdate && can(perms, "payables", "update");
  return (
    <>
      <ContentHeader
        title="DAS MEI"
        breadcrumb={["Contabilidade", "DAS MEI"]}
        actions={
          o ? (
            <a
              className="btn btn-outline-secondary"
              href={o.pgmeiUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir o PGMEI
            </a>
          ) : undefined
        }
      />
      <QueryStatus loading={ov.isLoading} error={ov.error} />
      {o && !o.isMei && (
        <div className="alert alert-info">
          O DAS MEI é só para empresa MEI (CRT 4 ou regime MEI em Preferências › Dados da empresa).
        </div>
      )}
      {o?.isMei && (
        <div className="box card mb-3">
          <div className="card-body">
            {canUpdate && <ActivityForm key={o.activity ?? ""} current={o.activity} />}
            {o.preview && (
              <p className="mb-2" data-testid="das-preview">
                Competência {competenceLabel(o.preview.competence)}: INSS {formatBRL(o.preview.inssCents)}
                {o.preview.icmsCents > 0 && ` + ICMS ${formatBRL(o.preview.icmsCents)}`}
                {o.preview.issCents > 0 && ` + ISS ${formatBRL(o.preview.issCents)}`} ={" "}
                <strong>{formatBRL(o.preview.totalCents)}</strong>, vence em {formatDateBR(o.preview.dueDate)}
                .
              </p>
            )}
            {can(perms, "accounting", "create") && can(perms, "payables", "create") && (
              <div className="d-flex flex-wrap gap-2 align-items-end">
                <Field label="Competência" className="mb-0">
                  <select
                    className="form-select"
                    value={competence}
                    onChange={(e) => setCompetence(e.target.value)}
                  >
                    {competenceOptions().map((c) => (
                      <option key={c} value={c}>
                        {competenceLabel(c)}
                      </option>
                    ))}
                  </select>
                </Field>
                <button
                  type="button"
                  className="btn btn-primary mb-3"
                  disabled={!o.activity || generate.isPending}
                  onClick={() => generate.mutate()}
                >
                  Gerar DAS
                </button>
              </div>
            )}
            <p className="small text-muted mb-0">
              A guia oficial sai do PGMEI (Receita Federal): emita lá e informe aqui o código de barras.
              Valores pela tabela do ano; atraso tem multa e juros calculados só pelo PGMEI.
            </p>
          </div>
        </div>
      )}
      {o?.isMei && (
        <div className="box card">
          {o.data.length === 0 ? (
            <EmptyState
              title="Nenhum DAS"
              description="Gere o DAS do mês para lançar a conta a pagar."
              actions={[]}
            />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Guias DAS MEI">
                <thead>
                  <tr>
                    <th scope="col">Competência</th>
                    <th scope="col">Vencimento</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Código</th>
                    <th scope="col">Situação</th>
                    <th scope="col">
                      <span className="visually-hidden">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {o.data.map((d) => {
                    const open = d.status === "pending" || d.status === "issued";
                    const busy = !!d.paymentStatus && PAYMENT_BUSY.has(d.paymentStatus);
                    const unknown = d.paymentStatus === "unknown";
                    return (
                      <tr key={d.id} data-testid={`das-${d.competence}`}>
                        <td>{competenceLabel(d.competence)}</td>
                        <td className="text-nowrap">
                          {formatDateBR(d.dueDate)}
                          {d.overdue && <span className="badge text-bg-danger ms-1">Vencido</span>}
                        </td>
                        <td className="text-end">{formatBRL(d.totalCents)}</td>
                        <td className="small">{collectionLineLabel(d.digitableLine)}</td>
                        <td>
                          <StatusBadge tone={DAS_STATUS_TONE[d.status]}>
                            {DAS_STATUS_LABEL[d.status]}
                          </StatusBadge>
                          {busy && !unknown && (
                            <div className="small text-muted">Pagamento no banco em andamento</div>
                          )}
                          {unknown && (
                            <div className="small text-danger" data-testid="das-payment-unknown">
                              Pagamento no banco a conferir: {d.paymentError ?? "resultado incerto"}
                            </div>
                          )}
                          {d.paymentStatus === "failed" && d.paymentError && (
                            <div className="small text-danger">{d.paymentError}</div>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          {open && canUpdate && !busy && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setDialog({ kind: "issue", das: d })}
                            >
                              {d.barcode ? "Trocar código" : "Informar código"}
                            </button>
                          )}
                          {open && canPay && !busy && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success ms-1"
                              onClick={() => setDialog({ kind: "pay", das: d })}
                            >
                              Marcar pago
                            </button>
                          )}
                          {d.status === "issued" && canPay && !busy && !d.overdue && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary ms-1"
                              onClick={() => setDialog({ kind: "bank", das: d })}
                            >
                              Pagar pelo banco
                            </button>
                          )}
                          {busy && canUpdate && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary ms-1"
                              disabled={refresh.isPending}
                              onClick={() => refresh.mutate(d.id)}
                            >
                              Consultar
                            </button>
                          )}
                          {unknown && canPay && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger ms-1"
                              onClick={() => setDialog({ kind: "release", das: d })}
                            >
                              Liberar
                            </button>
                          )}
                          {open && canPay && !busy && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger ms-1"
                              onClick={() => setDialog({ kind: "cancel", das: d })}
                            >
                              Cancelar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <DasDialog dialog={dialog} onClose={() => setDialog(null)} />
    </>
  );
}
